import React, { useEffect, useRef, useState } from "react";

import { Tooltip } from "@mui/material";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import SkeletonComponent from "../../Generic/Skeleton";
import useStyles from "./styles";

export interface FieldViewProps {
  label?: string;
  labelClass?: string;
  tooltip?: string;
  value?: boolean | number | string | Array<string>;
  valueClass?: string;
  emptyValueInfo?: string;
  "data-cy"?: string;
  loading?: boolean;
  multipleLines?: boolean;
  collapse?: boolean;
  icon?: React.ReactNode;
}

export default function FieldView(props: FieldViewProps) {
  const {
    label,
    labelClass,
    value = "",
    valueClass,
    tooltip,
    emptyValueInfo,
    loading = false,
    multipleLines = false,
    collapse = true,
    icon,
    ...restViewProps
  } = props;
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const valueRef = useRef<HTMLSpanElement>(null);
  const labelRef = useRef<HTMLLabelElement>(null);
  const [isValueOverflown, setIsValueOverflown] = useState(false);
  const [isLabelOverflown, setIsLabelOverflown] = useState(false);

  useEffect(() => {
    const element = valueRef.current!;

    if (element) setIsValueOverflown(element.scrollWidth > element.clientWidth);
  }, []);

  useEffect(() => {
    const element = labelRef.current!;

    if (element) setIsLabelOverflown(element.scrollWidth > element.clientWidth);
  }, []);

  const renderEmptyInfo = () => {
    return (
      <span className={classnames(classes.span, classes.emptySpan, classes.ellipsis)}>
        {emptyValueInfo || translate("empty")}
      </span>
    );
  };

  const renderWithIcon = (content: React.ReactNode) => (
    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
      {content}
      {icon && <span>{icon}</span>}
    </div>
  );

  const renderArray = () => {
    if (!Array.isArray(value) || !value?.length) return "";
    if (tooltip) {
      const newArray: string[] = [...value, `${tooltip.split(",").length} more`];

      return newArray.map((item, index: number) => (
        <>
          {index === newArray.length - 1 ? (
            <Tooltip title={tooltip} placement="top">
              <span key={item} className={classnames(valueClass, classes.span)}>
                {item}
              </span>
            </Tooltip>
          ) : (
            <span key={item} className={classnames(valueClass, classes.span)}>
              {item}
            </span>
          )}
        </>
      ));
    }

    return value.map((item) => (
      <span key={item} className={classnames(valueClass, classes.span)}>
        {item}
      </span>
    ));
  };

  const renderMultipleLines = () => {
    if (!Array.isArray(value) || !value?.length) return "";

    if (tooltip) {
      const newArray: string[] = [...value, `${tooltip.split(",").length} more`];

      return newArray.map((item, index: number) => (
        <>
          {index === newArray.length - 1 ? (
            <Tooltip title={tooltip} placement="top">
              <div key={item} className={classnames(valueClass, classes.span)}>
                {item}
              </div>
            </Tooltip>
          ) : (
            <div key={item} className={classnames(valueClass, classes.span)}>
              {item}
            </div>
          )}
        </>
      ));
    }

    return value.map((item) => (
      <div key={item} className={classnames(valueClass, classes.span)}>
        {item}
      </div>
    ));
  };

  const renderValue = () => {
    if (value instanceof Array && value.length) {
      const arrayContent = multipleLines ? (
        <div className={classes.multipleValuesWrapper}>{renderMultipleLines()}</div>
      ) : (
        <div className={classes.valueArrayWrapper}>{renderArray()}</div>
      );
      return renderWithIcon(arrayContent);
    }

    if (!value) return renderWithIcon(renderEmptyInfo());

    if (!collapse) {
      return renderWithIcon(
        <span ref={valueRef} className={classnames(valueClass, classes.span)}>
          {value}
        </span>
      );
    }

    return renderWithIcon(
      <Tooltip title={isValueOverflown ? value : ""} placement="top">
        <span ref={valueRef} className={classnames(valueClass, classes.span, classes.ellipsis)}>
          {value}
        </span>
      </Tooltip>
    );
  };

  return (
    <SkeletonComponent isLoading={loading}>
      <div className={classes.wrapper} {...restViewProps}>
        {label ? (
          <Tooltip title={isLabelOverflown ? label : ""} placement="top">
            <label ref={labelRef} className={classnames(classes.label, labelClass)}>
              {label}
            </label>
          </Tooltip>
        ) : null}
        {renderValue()}
      </div>
    </SkeletonComponent>
  );
}
